import { HttpClient, HttpHeaders, HttpEventType, HttpRequest } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { AppService } from 'src/@core/services/app.service';
import { NotificationService } from 'src/@core/services/notification.service';
import { API_GET_UPLOAD_URL, API_WEB_SOCKET, PROJECT_API_KEY } from 'src/@core/services/util/api.endpoints';
import { WebSocketService } from 'src/@core/services/websocket.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'hour4u-admin-call-agent',
  templateUrl: './call-agent.component.html',
  styleUrls: ['./call-agent.component.scss']
})
export class CallAgentComponent implements OnInit {
  excelFileKeys: string[] = [];
  selectedFiles: File[] = [];
  isLoading: boolean = false;
  progress: number;
  progressText: string;
  profile = this.appService.getCurrentAccount();
  numberOfChampsAdded: any;
  termsAccepted: boolean = false;
  uploadStarted: boolean = false;
  uploadViaTemplate: boolean = true; // Default to pre-defined template (no AI)
  @Input() data: any = {};
  @Output() uploadComplete: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() coraStateChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() triggerProjectPolling: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('dragDropUpload', { static: false }) dragDropUploadComponent;

  constructor(
    private http: HttpClient,
    @Optional() public dialogRef: MatDialogRef<CallAgentComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private injectedData: any,
    private appService: AppService,
    private notify: NotificationService,
    private wsService: WebSocketService
  ) { }

  ngOnInit() {
    if (!this.data || Object.keys(this.data).length === 0) {
      this.data = this.injectedData || {};
    }
    console.log('CallAgentComponent received data:', this.data);

    this.wsService.messages$.subscribe((msg: any) => {
      if ((msg && msg.type === 'upload_parsing_success') || (msg && msg.status === 'parsed')) {
        console.log('✅ Leads parsed successfully via WebSocket');
        this.coraStateChange.emit('finished');
      }
    });
  }

  handleFileUploaded(event: { key: string, file: File }) {
    // Skip storing key if empty (when skipUpload is true)
    if (event.key) {
      this.excelFileKeys.push(event.key);
    }
    this.selectedFiles.push(event.file);
    console.log('Added file:', event.file.name);
  }

  handleFileDeleted() {
    this.excelFileKeys = [];
    this.selectedFiles = [];
  }

  // Method to create bulk work order
  createBulkWorkOrder(): void {
    if (!this.selectedFiles.length) {
      console.error('No file selected!');
      return;
    }

    this.uploadStarted = true;
    this.isLoading = true;

    this.uploadFile().then(() => {
      this.selectedFiles = [];
      this.excelFileKeys = [];

      if (this.dragDropUploadComponent) {
        this.dragDropUploadComponent.reset();
      }

      this.uploadComplete.emit(true); // ✅ Always emit, even if dialog
      this.triggerProjectPolling.emit();

      if (this.dialogRef && this.dialogRef.close) {
        console.log('✅ Closing dialog with success');
        this.dialogRef.close(true);
      } else {
        console.log('✅ Upload complete event emitted (inline usage)');
      }
    }).catch(error => {
      console.error('Error during data update:', error);
      this.isLoading = false;
    });
  }

  uploadFile(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const authToken = localStorage.getItem("jwtToken");
      const uploadedFiles: any[] = [];

      try {
        for (let i = 0; i < this.selectedFiles.length; i++) {
          const file = this.selectedFiles[i];

          // Step 1: Get presigned upload URL from backend
          const uploadUrlResponse = await this.getUploadUrl(file, i + 1);

          if (!uploadUrlResponse.uploadUrl || !uploadUrlResponse.s3_key) {
            throw new Error('Failed to get upload URL');
          }

          // Step 2: Upload file directly to S3 using presigned URL
          await this.uploadToS3(file, uploadUrlResponse.uploadUrl, uploadUrlResponse.headers || {}, i + 1);

          // Store file info for WebSocket payload - match backend expected format
          uploadedFiles.push({
            filename: file.name,
            s3_key: uploadUrlResponse.s3_key,
            bucket: uploadUrlResponse.bucket || 'leads-files-bucket'
          });
        }

        // Step 3: Send WebSocket payload after all uploads complete
        // Backend will process files asynchronously
        this.wsService.sendUploadLeadsPayload(
          API_WEB_SOCKET,
          uploadedFiles,
          this.data.projectId,
          this.profile.entity.id,
          authToken,
          this.uploadViaTemplate
        );

        resolve();
      } catch (error) {
        console.error('Error during file upload:', error);
        reject(error);
      }
    });
  }

  private getUploadUrl(file: File, fileIndex: number): Promise<{
    uploadUrl: string;
    s3_key: string;
    bucket?: string;
    method?: string;
    headers?: { [key: string]: string };
    expiresIn?: number;
  }> {
    return new Promise((resolve, reject) => {
      const apiUrl = API_GET_UPLOAD_URL;

      const headers = new HttpHeaders({
        'x-api-key': PROJECT_API_KEY,
        'Content-Type': 'application/json'
      });

      const payload = {
        project_id: this.data.projectId,
        entity_id: this.profile.entity.id,
        filename: file.name,
        contentType: file.type || 'application/octet-stream'
      };

      this.http.post(apiUrl, payload, { headers }).subscribe(
        (response: any) => {
          console.log(`✅ Got upload URL for file ${fileIndex}:`, response);
          resolve({
            uploadUrl: response.uploadUrl,
            s3_key: response.s3_key,
            bucket: response.bucket,
            method: response.method,
            headers: response.headers || {},
            expiresIn: response.expiresIn
          });
        },
        error => {
          console.error(`❌ Error getting upload URL for file ${fileIndex}:`, error);
          reject(error);
        }
      );
    });
  }

  private uploadToS3(
    file: File,
    uploadUrl: string,
    headers: { [key: string]: string },
    fileIndex: number
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const fileProgress = Math.round((100 * event.loaded) / event.total);
          const totalProgress = Math.round(
            ((fileIndex - 1) * 100 + fileProgress) / this.selectedFiles.length
          );
          this.progress = totalProgress;
          console.log(`Uploading (${fileIndex}/${this.selectedFiles.length})... ${fileProgress}%`);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          console.log(`✅ File ${fileIndex} uploaded to S3 successfully`);
          resolve();
        } else {
          console.error(`❌ Error uploading file ${fileIndex} to S3:`, xhr.status, xhr.statusText);
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        console.error(`❌ Network error uploading file ${fileIndex} to S3`);
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        console.warn(`⚠️ Upload of file ${fileIndex} aborted`);
        reject(new Error('Upload aborted'));
      });

      // PUT file to S3 presigned URL
      xhr.open('PUT', uploadUrl);

      // Set headers from backend response
      Object.keys(headers).forEach(key => {
        xhr.setRequestHeader(key, headers[key]);
      });

      xhr.send(file);
    });
  }

  downloadSampleFile(event: Event) {
    event.stopPropagation();
    const link = document.createElement('a');
    link.href = 'assets/sample-file/calling_leads.xlsx';
    link.download = 'calling_leads.xlsx';
    link.click();
  }

  copyApplicationLink(event: Event) {
    event.stopPropagation();

    const url = environment.production
      ? `https://apply.houseofagents.ai/?project_id=${this.data.projectId}&entity_id=${this.profile.entity.id}`
      : `https://apply-dev.houseofagents.ai/?project_id=${this.data.projectId}&entity_id=${this.profile.entity.id}`;

    navigator.clipboard.writeText(url).then(() => {
      console.log('Link copied:', url);
      this.notify.showMessage(
        "Link copied",
        5000,
        'assets/tick-circle.svg',
        `Share URL with leads to allow them to submit resume!`
      );
    }).catch(err => {
      console.error('Failed to copy link:', err);
    });
  }
}